import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API, getToken } from "../lib/api";
import AppLayout from "../components/AppLayout";
import { STATUS_OPTIONS } from "../lib/fields";
import { toast } from "sonner";

const ROUTE_OPTIONS = [
  { value: "", label: "Any" },
  { value: "Skilled Worker (Switching immigration category - ISC liable)", label: "Skilled Worker (Switching immigration category - ISC liable)" },
  { value: "Skilled Worker (New hires - defined)", label: "Skilled Worker (New hires - defined)" },
];

export default function CosSearch() {
  const [criteria, setCriteria] = useState({
    certificate_number: "",
    status: "",
    family_name: "",
    given_names: "",
    date_of_birth: "",
    nationality: "",
    tier_and_category: "",
  });
  const [all, setAll] = useState([]);
  const [results, setResults] = useState(null); // null before first search
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/cos");
        setAll(data);
      } catch {
        toast.error("Failed to load certificates");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k, v) => setCriteria((p) => ({ ...p, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const c = criteria;
    const match = (row, key) => {
      const v = (c[key] || "").trim().toLowerCase();
      if (!v) return true;
      const cell = (row[key] || "").toString().toLowerCase();
      return cell.includes(v);
    };
    const filtered = all.filter(
      (r) =>
        match(r, "certificate_number") &&
        (c.status ? (r.current_certificate_status || "") === c.status : true) &&
        match(r, "family_name") &&
        match(r, "given_names") &&
        match(r, "date_of_birth") &&
        match(r, "nationality") &&
        (c.tier_and_category ? (r.tier_and_category || "") === c.tier_and_category : true)
    );
    setResults(filtered);
  };

  const reset = () => {
    setCriteria({
      certificate_number: "",
      status: "",
      family_name: "",
      given_names: "",
      date_of_birth: "",
      nationality: "",
      tier_and_category: "",
    });
    setResults(null);
  };

  const downloadPdf = async (id, cert) => {
    try {
      const res = await fetch(`${API}/cos/${id}/pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = cert ? `CoS-${cert}.pdf` : `CoS.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download PDF");
    }
  };

  const statusClass = (s) => {
    const k = (s || "").toLowerCase();
    if (k.includes("ready")) return "ready";
    if (k.includes("assign")) return "assigned";
    if (k.includes("used")) return "used";
    return "";
  };

  return (
    <AppLayout
      activeMenu="search-cos"
      breadcrumb={[
        { label: "Sponsorship management system", to: "/" },
        { label: "Workers", to: "/cos" },
        { label: "View CoS", to: "/cos" },
        { label: "Search for CoS" },
      ]}
    >
      <h1 data-testid="cos-search-heading">Search for CoS</h1>
      <p>
        Enter one or more criteria below and choose <strong>Search</strong> to find
        certificates that match. Leave a field blank to include all values for
        that field.
      </p>

      <form onSubmit={submit} data-testid="cos-search-form">
        <div className="sms-panel">
          <div className="sms-panel-title">Search criteria</div>
          <div className="sms-panel-body">
            <div className="sms-field-row">
              <label className="sms-field-label" htmlFor="s-cert">Certificate number:</label>
              <input
                id="s-cert" data-testid="search-certificate_number" className="sms-input"
                value={criteria.certificate_number}
                onChange={(e) => set("certificate_number", e.target.value)}
              />
            </div>
            <div className="sms-field-row">
              <label className="sms-field-label" htmlFor="s-status">Current status:</label>
              <select
                id="s-status" data-testid="search-status" className="sms-select"
                value={criteria.status}
                onChange={(e) => set("status", e.target.value)}
              >
                <option value="">Any</option>
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="sms-field-row">
              <label className="sms-field-label" htmlFor="s-family">Family name:</label>
              <input
                id="s-family" data-testid="search-family_name" className="sms-input"
                value={criteria.family_name}
                onChange={(e) => set("family_name", e.target.value)}
              />
            </div>
            <div className="sms-field-row">
              <label className="sms-field-label" htmlFor="s-given">Given name(s):</label>
              <input
                id="s-given" data-testid="search-given_names" className="sms-input"
                value={criteria.given_names}
                onChange={(e) => set("given_names", e.target.value)}
              />
            </div>
            <div className="sms-field-row">
              <label className="sms-field-label" htmlFor="s-dob">Date of birth:</label>
              <input
                id="s-dob" data-testid="search-date_of_birth" className="sms-input"
                placeholder="dd/mm/yyyy"
                value={criteria.date_of_birth}
                onChange={(e) => set("date_of_birth", e.target.value)}
              />
            </div>
            <div className="sms-field-row">
              <label className="sms-field-label" htmlFor="s-nat">Nationality:</label>
              <input
                id="s-nat" data-testid="search-nationality" className="sms-input"
                value={criteria.nationality}
                onChange={(e) => set("nationality", e.target.value)}
              />
            </div>
            <div className="sms-field-row">
              <label className="sms-field-label" htmlFor="s-route">Route:</label>
              <select
                id="s-route" data-testid="search-tier_and_category" className="sms-select"
                value={criteria.tier_and_category}
                onChange={(e) => set("tier_and_category", e.target.value)}
              >
                {ROUTE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="sms-actionbar">
          <div className="sms-actionbar-left">
            <Link to="/cos" className="sms-btn secondary" data-testid="search-back-btn">
              Back
            </Link>
            <button
              type="button" data-testid="search-reset-btn"
              onClick={reset} className="sms-btn secondary"
            >
              Reset
            </button>
          </div>
          <button type="submit" data-testid="search-submit-btn" className="sms-btn">
            Search
          </button>
        </div>
      </form>

      {loading ? null : results !== null && (
        <div className="sms-panel" data-testid="search-results-panel">
          <div className="sms-panel-title">
            Search results {results.length > 0 && <span style={{ fontWeight: "normal" }}>· {results.length} match{results.length === 1 ? "" : "es"}</span>}
          </div>
          <div className="sms-panel-body" style={{ padding: 0 }}>
            {results.length === 0 ? (
              <div style={{ padding: 16, color: "#666" }}>No certificates match those criteria.</div>
            ) : (
              <table className="sms-table" data-testid="search-results-table">
                <thead>
                  <tr>
                    <th>Certificate number</th>
                    <th>Status</th>
                    <th>Family name</th>
                    <th>Given name</th>
                    <th>Date of birth</th>
                    <th>Nationality</th>
                    <th>Route</th>
                    <th style={{ textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((c) => (
                    <tr key={c.id} data-testid={`search-row-${c.id}`}>
                      <td>
                        <Link to={`/cos/${c.id}`} style={{ fontFamily: "monospace" }}>
                          {c.certificate_number || "—"}
                        </Link>
                      </td>
                      <td>
                        <span className={`sms-status-pill ${statusClass(c.current_certificate_status)}`}>
                          {c.current_certificate_status || "—"}
                        </span>
                      </td>
                      <td>{c.family_name || "—"}</td>
                      <td>{c.given_names || "—"}</td>
                      <td>{c.date_of_birth || "—"}</td>
                      <td>{c.nationality || "—"}</td>
                      <td style={{ maxWidth: 180 }}>{c.tier_and_category || "—"}</td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <button
                          type="button"
                          data-testid={`search-pdf-${c.id}`}
                          onClick={() => downloadPdf(c.id, c.certificate_number)}
                          className="sms-btn small secondary"
                        >
                          PDF
                        </button>{" "}
                        <button
                          type="button"
                          data-testid={`search-edit-${c.id}`}
                          onClick={() => navigate(`/cos/${c.id}`)}
                          className="sms-btn small secondary"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
